import { Chapter } from '@/interfaces/IResellerAcademy';
import { Theme } from '@/interfaces/IThemeInterface';
import { Features, Reseller, Tours, Webhook, WebhookEvent, WebhookType } from '@/types/TReseller';
import apiClient from '@/utils/apiClient';
import { ResellerCreditDetails } from '../interfaces';
import { User, UserAccount } from '@/types/TAccounts';
import { USER_TYPES } from 'src/enums/EUser';

const RESELLER_FORGOT_PASSWORD = '/api/reseller/forgot-password';
const RESELLER_CAA_CHECK = '/api/reseller/check-caa';
const RESELLER_UPDATE_DOMAIN = '/api/reseller/update-domain';
const RESELLER_DELETE_DOMAIN = '/api/reseller/delete-domain';
const RESELLER_DNS_STATUS = '/api/reseller/dns-status';
const RESELLER_DNS_RECORD = '/api/reseller/dns-record';
const RESELLER_VALID_PASSWORD_LINK = '/api/reseller/reset-password-link-valid';
const RESELLER_RESET_PASSWORD = '/api/reseller/reset-password';
const UPDATE_RESELLER_ONBOARDING = '/api/reseller/update_reseller_onboarding_status';
const SAVE_RESELLER_QUESTIONNAIRE_DATA = '/api/reseller/questionnaire';
const RESELLER_DASHBOARD_LINK_API_PATH = '/get-dashboard-link';
const UPDATE_AUTO_SCALE_RECEPTIONIST_API_PATH = '/api/reseller/update-auto-scale-receptionist';
const UPDATE_RESELLER_BRANDING = '/update_reseller_business_name_and_logo';
const RESELLER_TEST_EMAIL = '/api/v2/receptionist-dashboard/reseller-test-email';
const UPDATE_RESELLER_BRANDING_TOGGLE = '/api/reseller/branded/setting/update';
const Fetch_Reseller_Clients_API_PATH = '/fetch_reseller_clients';
const Fetch_Reseller_TEST_Clients_API_PATH = '/fetch_reseller_test_clients';
const DELETE_RECEPTIONIST_API_PATH = '/api/reseller/delete-receptionist';
const UPDATE_MANAGED_RECEPTIONIST_API_PATH = '/api/reseller/update-managed-receptionist';
const CLEAN_DATA_OF_MANAGED_RECEPTIONIST_API_PATH =
	'/api/reseller/clean-data-of-managed-receptionist';
const CREATE_CHECKOUT_SESSION_NEW_RESELLERS_API_PATH = '/create-checkout-session-new-resellers';
const BILLING_OVERVIEW_API_PATH = '/api/reseller/billing-overview';
const HANDLE_CUSTOMER_PORTAL_API_PATH = '/api/stripe/customerPortal/reseller';
const FETCH_PENDING_INVOICES_API_PATH = '/api/reseller/fetch-pending-invoices';
const IMPORTED_NUMBERS_API_PATH = '/api/reseller/twilio-imported-numbers';
const REMOVE_CLIENT_NUMBER_API_PATH = '/api/reseller/remove-client-number';
const ASSIGN_CLIENT_NUMBER_API_PATH = '/api/reseller/assign-client-number';
const CONVERT_TRIAL_RECEPTIONIST = '/api/reseller/convert_trial_receptionist';
const DECREMENT_UNRESERVED_RECEPTIONIST = '/api/reseller/remove-subscription';
const GENERATE_DASHBOARD_THEME_AI = '/api/reseller/generate-dashboard-theme-ai';
const SET_DASHBOARD_THEME = '/api/reseller/set-dashboard-theme';
const REMOVE_DASHBOARD_THEME = '/api/reseller/remove-dashboard-theme';
const REMOVE_DISCOUNT_API_PATH = '/api/v2/receptionist-dashboard/remove-discount';
const APPLY_COUPON_PATH = '/api/v2/receptionist-dashboard/apply-coupon';
const RESELLER_ACADEMY = '/api/reseller/academy';
const CHECK_ACCOUNT_EXIST = '/api/reseller/check-existing-account';
const CREATE_CONNECT_ACCOUNT = '/api/reseller/create-connect-account';
const CREATE_PLAN = '/api/reseller/create-plan';
const DELETE_PLAN = '/api/reseller/delete-plan';
const UN_ARCHIVE = '/api/reseller/unArchivePlan';
const GET_CONNECT_ACCOUNT = '/api/reseller/get-connect-account';
const GET_RESELLER_METERS = '/api/reseller/getallMeters';
const CREATE_RESELLER_METERS = '/api/reseller/create-meter';
const CREATE_CLIENT_CHECKOUT_SESSION = '/api/reseller/checkout';
const SUBSCRIPTION_UNIT_PRICE = '/api/reseller/subscription-unit-price';
const UPDATE_RESELLER_CLIENT_SUBSCRIPTION = '/api/reseller/update-plan';
const END_RESELLER_CLIENT_SUBSCRIPTION = '/api/reseller/cancel-plan';
const GET_RESOURCE_URL = (key: string) => `/api/reseller/academy/resource/${key}`;
const GET_RESELLER_PLAN_BY_ID = '/api/reseller/getPlanById';
const GET_RESELLER_PLANS = '/api/reseller/getAllPlans';
export const UPDATE_RESELLER_LOGO = `${import.meta.env.VITE_APP_API_BASE_URL}/upload_reseller_logo`;
const DOWNLOAD_CALL_LOGS = '/api/reseller/download/call-logs';
const CUSTOM_OFFER_API_PATH = '/api/reseller/create_reseller_customOffer';
const CANCEL_RESELLER_SUBSCRIPTION = '/api/reseller/cancel-subscription-reseller';
const GET_CLIENT_CREDITS = '/api/reseller/credits';
const ADD_RESELLER_TO_WAITLIST = '/api/reseller/add-to-waitlist';
const END_TRIAL = '/api/stripe/endingTrial';
const AUTO_RELOAD_CREDITS = (clientId: string) => `/api/reseller/credits/${clientId}/auto-reload`;
const BUY_CREDITS = (clientId: string) => `/api/reseller/credits/${clientId}/buy`;
const UPDATE_TUTORIAL_STATUS = '/api/reseller/update-tutorial-status';
const UPDATE_TOUR_STATUS = '/api/reseller/update-tour-status';
const RESELLER_DEFAULT_REGISTRATION = '/api/reseller/default-registration';
const CURRENT_SUBSCRIPTION_INFORMATION_PATH = '/api/reseller/current-subscription-information';
const UPDATE_INTERCOM_APP_ID = '/api/reseller/save-intercom-app-id';
const MANAGE_SUB_ACCOUNT_API_PATH = '/api/reseller/manage-sub-account';
const COPY_SUB_ACCOUNT_API_PATH = '/api/reseller/copy-sub-account';

// Webhook API endpoints
const GET_WEBHOOKS = '/api/reseller/webhooks';
const CREATE_WEBHOOK = '/api/reseller/webhook';
const UPDATE_WEBHOOK = (webhookId: string) => `/api/reseller/webhook/${webhookId}`;
const DELETE_WEBHOOK = (webhookId: string) => `/api/reseller/webhook/${webhookId}`;
const TEST_WEBHOOK = (webhookId: string) => `/api/reseller/webhook/${webhookId}/test`;

const CREATE_USER_API_PATH = '/api/users/register';
const GET_USER_DATA_API_PATH = '/api/users';
const USER_LOGIN_API_PATH = '/api/users/login';

export const resellerLogin = async (payload: {
	username: string;
	password: string;
	from: USER_TYPES.RESELLER;
}): Promise<{
	success: boolean;
	data?: Reseller;
	token?: string;

	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: USER_LOGIN_API_PATH,
		body: payload,
	});

	return response;
};

export const resellerUserExists = async (payload: {
	email_address: string;
}): Promise<{ resellerUserExists: boolean; message: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: RESELLER_FORGOT_PASSWORD,
		body: payload,
	});

	return response;
};

type CAARecord = {
	critical: number;
	issue?: string;
	issuewild?: string;
};

export const checkCAA = async (payload: {
	domain: string | undefined;
}): Promise<{ domainStored: boolean; caaRecord: CAARecord[] | null; message: string }> => {
	try {
		const response = await apiClient.request({
			method: 'POST',
			endPoint: RESELLER_CAA_CHECK,
			clientType: 'RESELLER',
			body: payload,
		});

		return response;
	} catch (err: any) {
		console.error('❌ Error in checkCAA:', err);

		if (err?.response?.data?.message) {
			// Backend sent a custom error message
			return {
				domainStored: false,
				caaRecord: null,
				message: `⚠️ ${err.response.data.message}`,
			};
		}

		// Fallback for unknown errors
		return {
			domainStored: false,
			caaRecord: null,
			message: '❌ Something went wrong while checking CAA records. Please try again later.',
		};
	}
};

export const updateDomain = async (payload: {
	domain: string;
}): Promise<{ success: boolean; message: string; domainUpdated: boolean; vercelResponse: any }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: RESELLER_UPDATE_DOMAIN,
		clientType: 'RESELLER',
		body: payload,
	});

	return response;
};

export const deleteDomain = async (payload: {
	domain: string;
}): Promise<{ domainDeleted: boolean; amplifyResponse: any; message: string }> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: RESELLER_DELETE_DOMAIN,
		clientType: 'RESELLER',
		body: payload,
	});

	return response;
};

export const dnsStatus = async (payload: {
	domain: string | undefined;
}): Promise<{ dnsStausFetched: boolean; dnsStatus: any; message: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: RESELLER_DNS_STATUS,
		clientType: 'RESELLER',
		body: payload,
	});

	return response;
};

export const dnsRecord = async (payload: {
	domain: string | undefined;
	appId: string | undefined;
}): Promise<{ dnsRecordFetched: boolean; dnsRecord: any; message: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: RESELLER_DNS_RECORD,
		clientType: 'RESELLER',
		body: payload,
	});

	return response;
};

export const isResellerResetPasswordLinkValid = async (payload: {
	email_address: string;
	token: string;
}): Promise<{ isValid: boolean }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: RESELLER_VALID_PASSWORD_LINK,
		body: payload,
	});

	return response;
};

export const resellerResetPassword = async (payload: {
	email_address: string;
	password: string;
}): Promise<{ success: boolean }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: RESELLER_RESET_PASSWORD,
		body: payload,
	});

	return response;
};

export const getReseller = async ({
	from,
}: {
	from: USER_TYPES.RESELLER;
}): Promise<{
	success: boolean;
	data?: Reseller;
	token?: string;

	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: GET_USER_DATA_API_PATH,
		body: {
			from,
		},
		clientType: 'RESELLER',
	});

	return response;
};

export const ResellerDashboardLink = async (): Promise<{ success?: boolean; link: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: RESELLER_DASHBOARD_LINK_API_PATH,
		clientType: 'RESELLER',
	});

	return response;
};

export const UpdateAutoScaleReceptionist = async (payload: {
	reseller_email: string;
	auto_scale: boolean;
	customerId: string;
}): Promise<{ success: boolean }> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_AUTO_SCALE_RECEPTIONIST_API_PATH,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const UpdateResellerOnboardingStatus = async (payload: {
	emailAddress: string;
	onboardingStep: number;
	isOnboardingComplete?: boolean;
	hasCompletedOnboarding?: boolean;
}) => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: UPDATE_RESELLER_ONBOARDING,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const SaveResellerQuestionnaireData = async (payload: {
	clientCount: number;
	businessType: string;
	revenue?: string;
	teamSize?: string;
	websiteUrl?: string;
}) => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: SAVE_RESELLER_QUESTIONNAIRE_DATA,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const UpdateResellerBranding = async (payload: {
	emailAddress: string;
	privacyPolicyURL?: string;
	resellerName?: string;
	termsOfUseURL?: string;
	themeColor?: string;
	uploadedLogoS3Url?: string;
	purchaseRedirectLink?: string;
	logoDimensions?: { width: number; height: number };
	twilioAccessToken?: string;
	twilioAccountId?: string;
	featureVisibility?: Array<string>;
	featureGating?: Array<string>;
	featureTrialVisibility?: Array<string>;
	featureTrialGating?: Array<string>;
	features?: Features;
	resellerMailgunDomain?: string;
	resellerMailgunEmail?: string;
	resellerMailgunRegion?: string;
	resellerMailgunUsername?: string;
	resellerMailgunAPIKey?: string;
	webhook_url?: string;
}): Promise<{ success?: boolean }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: UPDATE_RESELLER_BRANDING,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const ResellerSendTestEmail = async (payload: {
	emailAddress: string;
	emailTo: string;
	emailContent: string;
}) => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: RESELLER_TEST_EMAIL,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const UpdateResellerBrandingToggle = async (payload: {
	userId: string;
	branded: boolean;
}): Promise<{ success: boolean; branded: boolean }> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_RESELLER_BRANDING_TOGGLE,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const fetchResellerClients = async (shouldFetchReservedNumbers: boolean): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: Fetch_Reseller_Clients_API_PATH,
		body: { shouldFetchReservedNumbers },
		clientType: 'RESELLER',
	});

	return response;
};
export const fetchResellerTestClients = async (): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: Fetch_Reseller_TEST_Clients_API_PATH,
		clientType: 'RESELLER',
	});

	return response;
};

export const deleteReceptionist = async (payload: {
	receptionist_username: string;
	reseller_email: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: DELETE_RECEPTIONIST_API_PATH,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const updateManagedReceptionist = async (payload: {
	receptionist_username: string;
	fields: Record<string, any>;
	userId?: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_MANAGED_RECEPTIONIST_API_PATH,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const cleanDataOfManagedReceptionist = async (payload: {
	receptionist_username: string;
	tableNames: string[];
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CLEAN_DATA_OF_MANAGED_RECEPTIONIST_API_PATH,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const createCheckoutSessionNewResellers = async (payload: {
	resellerEmail: string;
	quantity: number;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_CHECKOUT_SESSION_NEW_RESELLERS_API_PATH,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const getSubscriptionInformation = async (payload: {
	email_address: string;
}): Promise<{
	success: boolean;
	message?: string;
	data?: {
		basePlanStatus: string;
		outboundPlanStatus: string;
		voiceUsageCharge: number;
		subAccounts: {
			username: string;
			freeMinutesLeft: number;
			totalFreeMinutes: number;
			totalMinutesUsed: number;
		}[];
		appliedCoupons?:
			| {
					id: string;
					name: string;
					amountOff: number | null;
					percentOff: number | null;
					duration: string;
					durationInMonths: number | null;
					validUntil: string | null;
			  }[]
			| null;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: BILLING_OVERVIEW_API_PATH,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const endTrial = async (subscriptionId: string): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: END_TRIAL,
		body: { subscriptionId },
		clientType: 'RESELLER',
	});

	return response;
};

export const handleCustomerPortal = async (payload: { emailAddress: string }): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: HANDLE_CUSTOMER_PORTAL_API_PATH,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const fetchPendingInvoices = async (): Promise<{
	status: boolean;
	pendingInvoices?: any[];
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: FETCH_PENDING_INVOICES_API_PATH,
		clientType: 'RESELLER',
	});

	return response;
};

export const convertTrialReceptionist = async (payload: {
	reseller_email: string;
	agentId: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CONVERT_TRIAL_RECEPTIONIST,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};
export const getImportedTwilioNumbers = async (): Promise<any> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: IMPORTED_NUMBERS_API_PATH,
		clientType: 'RESELLER',
	});

	return response;
};

export const removeClientNumber = async (payload: {
	clientUsername: string;
	clientNumber: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: REMOVE_CLIENT_NUMBER_API_PATH,
		body: { ...payload, wantsToRelease: false },
		clientType: 'RESELLER',
	});

	return response;
};

export const assignClientNumber = async (payload: {
	clientUsername: string;
	clientNumber: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: ASSIGN_CLIENT_NUMBER_API_PATH,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const decrementUnreservedReceptionist = async (): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: DECREMENT_UNRESERVED_RECEPTIONIST,
		clientType: 'RESELLER',
	});

	return response;
};

export const generateDashboardThemeAI = async (payload: {
	logoKey: string;
	currentTheme: object;
}): Promise<{
	success: boolean;
	data?: {
		theme: Theme;
	};
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: GENERATE_DASHBOARD_THEME_AI,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const setDashboardTheme = async (payload: {
	theme: object;
}): Promise<{
	success: boolean;
	data?: {
		dashboardTheme: Theme;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: SET_DASHBOARD_THEME,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const resetThemeToDefault = async (): Promise<{
	success: boolean;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: REMOVE_DASHBOARD_THEME,
		clientType: 'RESELLER',
	});

	return response;
};

export const removeDiscount = async (payload: {
	appliedOn: string;
}): Promise<{
	success: boolean;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: REMOVE_DISCOUNT_API_PATH,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const applyCouponCode = async (
	couponCode: string,
): Promise<{ success: boolean; message?: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: APPLY_COUPON_PATH,
		body: { couponCode },
		clientType: 'RESELLER',
	});

	return response;
};

export const fetchResellerAcademy = async (): Promise<{
	success: boolean;
	data?: Chapter[];
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: RESELLER_ACADEMY,
		clientType: 'RESELLER',
	});

	return response;
};

export const updateResellerAcademyHistory = async (payload: {
	chapterId: number;
	videoSlug: string;
	isCompleted: boolean;
	watchedDuration: number;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: RESELLER_ACADEMY,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const getResourceURL = async (
	key: string,
): Promise<{
	success: boolean;
	url?: string;
	message?: string;
}> => {
	const repsonse = apiClient.request({
		method: 'GET',
		endPoint: GET_RESOURCE_URL(key),
		clientType: 'RESELLER',
	});

	return repsonse;
};

export const checkAccountExist = async (
	emailAddress: string,
): Promise<{
	success: boolean;
	message: string;
	exist?: boolean;
	name?: string;
	data?: Reseller;
	token?: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CHECK_ACCOUNT_EXIST,
		body: { emailAddress },
	});

	return response;
};

// =============================== reseller billing ===============================//

export const createConnectAccount = async (values: {
	email: string;
	country?: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_CONNECT_ACCOUNT,
		body: values,
		clientType: 'RESELLER',
	});

	return response;
};

export const createPlan = async (values: Record<string, any>): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_PLAN,
		body: values,
		clientType: 'RESELLER',
	});

	return response;
};

export const deletePlan = async (payload: { planId: string; connectId: string }): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: DELETE_PLAN,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const unArchive = async (payload: { planId: string; connectId: string }): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: UN_ARCHIVE,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const getConnectAccount = async (): Promise<any> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_CONNECT_ACCOUNT,

		clientType: 'RESELLER',
	});

	return response;
};

export const getResellerMeters = async (): Promise<any> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_RESELLER_METERS,
		clientType: 'RESELLER',
	});

	return response;
};

export const getResellerPlanById = async (subscribedProductId: string): Promise<any> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${GET_RESELLER_PLAN_BY_ID}/${subscribedProductId}`,
	});

	return response;
};

export const getAllResellerPlans = async (params?: {
	active?: boolean;
	resellerId: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_RESELLER_PLANS,
		params,
	});

	return response;
};

export const createMeter = async (values: Record<string, any>): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_RESELLER_METERS,
		body: values,
		clientType: 'RESELLER',
	});

	return response;
};

export const createClientCheckoutSession = async (values: { planId: string }): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_CLIENT_CHECKOUT_SESSION,
		body: values,
	});

	return response;
};

export const resellerClientUpdatePlan = async (values: {
	productId: string;
	stripe_subscriptionId: string;
	connectAccountId: string;
	agentId: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: `${UPDATE_RESELLER_CLIENT_SUBSCRIPTION}/${values.agentId}`,
		body: values,
	});

	return response;
};

export const resellerClientCancelPlan = async (): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: END_RESELLER_CLIENT_SUBSCRIPTION,
		// body: values,
	});

	return response;
};

export const getSubscriptionUnitPrice = async (): Promise<{
	success: boolean;
	unitPrice: number;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: SUBSCRIPTION_UNIT_PRICE,
		clientType: 'RESELLER',
	});

	return response;
};

export const downloadCallLogs = async (queryParam: {
	startDate?: number | null;
	endDate?: number | null;
	clientName: string;
}): Promise<void> => {
	let queryParamString = '?clientName=' + queryParam.clientName;

	if (queryParam.startDate) {
		queryParamString += `&startDate=${queryParam.startDate}`;
	}

	if (queryParam.endDate) {
		queryParamString += `&endDate=${queryParam.endDate}`;
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${DOWNLOAD_CALL_LOGS}${queryParamString}`,
		clientType: 'RESELLER',
	});

	const blob = new Blob([response.callReport], { type: 'text/csv' });
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `call_logs_${new Date().toISOString()}.csv`;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	window.URL.revokeObjectURL(url);
};

export const getClientCredits = async (
	userId: string,
): Promise<{
	success: boolean;
	data?: ResellerCreditDetails;
	error?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${GET_CLIENT_CREDITS}/${userId}`,
		clientType: 'RESELLER',
	});

	return response;
};

export const updateAutoReloadCredits = async (payload: {
	thresholdCredits: number;
	creditsToReload: number;
	autoReloadCredits: boolean;
	settingsFor: 'voice' | 'sms' | 'reseller-client-voice' | 'reseller-client-sms';
	clientId: string;
}): Promise<{
	success: boolean;
	data?: {
		thresholdCredits: number;
		creditsToReload: number;
		autoReloadCredits: boolean;
	};
	error?: string;
}> => {
	const { clientId, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: AUTO_RELOAD_CREDITS(clientId),
		body,
		clientType: 'RESELLER',
	});

	return response;
};

export const buyCredits = async (payload: {
	credits: number;
	creditsFor: 'voice' | 'sms' | 'reseller-client-voice' | 'reseller-client-sms';
	clientId: string;
}): Promise<{
	success: boolean;
	message?: string;
	error?: string;
}> => {
	const { clientId, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: BUY_CREDITS(clientId),
		body,
		clientType: 'RESELLER',
	});

	return response;
};
// =============================== reseller self serve ===============================//
// /sudo-register

export const sudoRegister = async (payload: {
	emailAddress: string;
	password?: string;
	businessName: string;
	clientLocation: string;
	username: string;
	type: 'Reseller';
	firstName: string;
	lastName: string;
	clientsCount: string;
	businessType: string;
	businessRevenue: string;
	businessEmployeeSize: string;
	businessWebsite: string;
	planToSell: string;
	testedReceptionist: string;
	premiumPartnershipCommitment: string;
}): Promise<{
	success: boolean;
	// Success response
	data?: Reseller;
	token?: string;

	// Error response
	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_USER_API_PATH,
		body: payload,
	});

	return response;
};

export const createCustomOffer = async (payload: {
	encodedData: string;
	id: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CUSTOM_OFFER_API_PATH,
		body: payload,
	});

	return response;
};

// add reseller to waitlist if the county they want to sell in is not listed in the support countries
export const addResellerToWaitlist = async (payload: {
	emailAddress: string;
	countryCode: string;
	country: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: ADD_RESELLER_TO_WAITLIST,
		body: payload,
	});

	return response;
};

export const cancelResellerSubscription = async (payload: {
	reason: string;
	subscriptionType: 'Inbound' | 'Outbound';
}): Promise<{ success: boolean; message: string; date?: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CANCEL_RESELLER_SUBSCRIPTION,
		body: payload,
		clientType: 'RESELLER',
	});

	return response;
};

export const updateTutorialStepStatus = async (stepName: string): Promise<{ success: boolean }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: UPDATE_TUTORIAL_STATUS,
		body: { stepName },
		clientType: 'RESELLER',
	});
	return response;
};

export const updateTourStatus = async (tourName: keyof Tours): Promise<{ success: boolean }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: UPDATE_TOUR_STATUS,
		body: { tourName },
		clientType: 'RESELLER',
	});
	return response;
};

export const updateDefaultRegistration = async (payload: {
	defaultRegistration: string;
}): Promise<{ message: string }> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: RESELLER_DEFAULT_REGISTRATION,
		clientType: 'RESELLER',
		body: payload,
	});

	return response;
};

export const currentSubscriptionInformation = async (): Promise<{
	status: string;
	data?: {
		currentStatus: string;
		trialEnd: string;
		allowExtendTrial: boolean;
		currentPeriodEnd: string;
		currentSubscriptionPrice: number;
		currentSubscriptionProduct: string;
		currentSubscriptionInterval: string;
	};
	error?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: CURRENT_SUBSCRIPTION_INFORMATION_PATH,
		clientType: 'RESELLER',
	});
	return response;
};

// =============================== reseller webhooks ===============================//

export const getWebhooks = async (): Promise<{
	success: boolean;
	data?: Webhook[];
	error?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_WEBHOOKS,
		clientType: 'RESELLER',
	});

	return response;
};

export const createWebhook = async (payload: {
	url: string;
	type: WebhookType;
	events: WebhookEvent[];
	description?: string;
	title: string;
}): Promise<{
	success: boolean;
	data?: Webhook;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_WEBHOOK,
		clientType: 'RESELLER',
		body: {
			url: payload.url,
			events: payload.events,
			description: payload.description,
			type: payload.type,
			title: payload.title,
		},
	});

	return response;
};

export const updateWebhook = async (
	webhookId: string,
	payload: Partial<Webhook>,
): Promise<{
	success: boolean;
	data?: Webhook;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_WEBHOOK(webhookId),
		clientType: 'RESELLER',
		body: payload,
	});

	return response;
};

export const deleteWebhook = async (
	webhookId: string,
): Promise<{
	success: boolean;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: DELETE_WEBHOOK(webhookId),
		clientType: 'RESELLER',
	});

	return response;
};

export const testWebhook = async (
	webhook: Webhook,
): Promise<{
	success: boolean;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: TEST_WEBHOOK(webhook.id),
		clientType: 'RESELLER',
		body: {
			callLogId: '123',
			callSid: '123',
			clientName: 'John Doe',
		},
	});

	return response;
};

export const updateIntercomAppId = async (payload: {
	intercom_app_id: string;
}): Promise<{
	success: boolean;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_INTERCOM_APP_ID,
		clientType: 'RESELLER',
		body: payload,
	});

	return response;
};

export const manageSubAccount = async ({
	clientId,
	agentId,
}: {
	clientId: string;
	agentId: string;
}): Promise<{
	success: boolean;
	data?: UserAccount;
	token?: string;
	agent?: User;

	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		endPoint: MANAGE_SUB_ACCOUNT_API_PATH,
		method: 'POST',
		clientType: 'RESELLER',
		body: { clientId, agentId },
	});

	return response;
};

export const copySubAccount = async ({
	emailAddress,
	username,
	password,
	agentId,
	businessName,
}: {
	emailAddress: string;
	username: string;
	password: string;
	agentId: string;
	businessName: string;
}) => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: COPY_SUB_ACCOUNT_API_PATH,
		clientType: 'RESELLER',
		body: {
			emailAddress,
			username,
			password,
			agentId,
			businessName,
		},
	});

	return response;
};
